/**
 * Vertical list of blue help links with a leading icon.
 */

import Link from '@/components/ui/Link';
import { usePostHog } from 'src/lib/posthog';
import type { HelpLinkItem } from './links';

interface HelpLinkListProps {
  links: HelpLinkItem[];
  className?: string;
  heading?: string;
}

function isExternalHref(href: string): boolean {
  return /^(https?:|mailto:|tel:)/i.test(href);
}

const LINK_TEXT = '!text-secondary-blue';

export default function HelpLinkList({
  links,
  className,
  heading = 'Onboarding Calls & Tutorials'
}: HelpLinkListProps) {
  const posthog = usePostHog();

  return (
    <div className={className}>
      {heading ? <h3 className="mb-1.5 text-sm font-semibold text-content-default">{heading}</h3> : null}
      <ul className="!m-0 flex !list-none flex-col gap-1.5 !p-0">
        {links.map((link) => {
          const Icon = link.icon;
          const content = (
            <>
              <Icon size={16} className="mt-0.5 shrink-0" aria-hidden />
              <span>{link.label}</span>
            </>
          );
          const rowClass = `inline-flex items-start gap-2 text-sm ${LINK_TEXT}`;

          const track = () => {
            link.onClick?.();
            posthog?.capture('help_link_clicked', { id: link.id, href: link.href });
          };

          if (!link.href) {
            return (
              <li key={link.id} className="!list-none">
                <span className={rowClass}>{content}</span>
              </li>
            );
          }

          if (isExternalHref(link.href)) {
            return (
              <li key={link.id} className="!list-none">
                <Link href={link.href} className={rowClass} onClick={track}>
                  {content}
                </Link>
              </li>
            );
          }

          return (
            <li key={link.id} className="!list-none">
              <Link to={link.href} className={rowClass} onClick={track}>
                {content}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
